import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, Book, ChevronRight, CheckCircle2 } from "lucide-react";

const CATEGORY_BADGES = {
  "Manual oficial": "bg-[#1B2E7A]/10 text-[#1B2E7A]",
  "Playbook NextGen": "bg-[#DC2626]/10 text-[#DC2626]",
  "Guía rápida": "bg-[#EAB308]/15 text-[#8C5E1A]",
  "General": "bg-[#F3F4F6] text-[#374151]",
};

function BookSpine({ book, idx }) {
  const accent = book.color_accent || "#1B2E7A";
  return (
    <Link
      to={`/biblioteca/${book.id}`}
      data-testid={`book-card-${book.id}`}
      className={`fade-up stagger-${(idx % 6) + 1} group relative bg-white border border-[#E5E7EB] rounded-md overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col`}
    >
      {book.is_read && (
        <span
          className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/95 text-[#1B2E7A] text-[10px] font-medium uppercase tracking-[0.15em] shadow-sm"
          data-testid={`book-read-badge-${book.id}`}
        >
          <CheckCircle2 className="h-3 w-3" /> Leído
        </span>
      )}
      {/* Book "cover" */}
      <div
        className="aspect-[3/4] relative overflow-hidden flex flex-col justify-between p-6"
        style={{
          background: `linear-gradient(155deg, ${accent} 0%, ${shade(accent, -15)} 100%)`,
        }}
      >
        {/* texture overlay */}
        <div
          className="absolute inset-0 opacity-20 mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 40%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.3), transparent 50%)",
          }}
        />
        {/* spine line */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-black/20" />
        <div className="absolute left-2 top-0 bottom-0 w-px bg-white/30" />

        <div className="relative z-10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 mb-3">
            Mueblería Canales
          </p>
          <Book className="h-7 w-7 text-white/80" strokeWidth={1.3} />
        </div>

        <div className="relative z-10">
          <h3 className="font-heading text-xl text-white leading-tight font-medium">
            {book.title}
          </h3>
          {book.subtitle && (
            <p className="text-xs text-white/70 mt-2 line-clamp-2">{book.subtitle}</p>
          )}
        </div>

        <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/60">
          <span>{book.estimated_pages || "—"} pp</span>
          <span>Tomo {String(idx + 1).padStart(2, "0")}</span>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <span
          className={`inline-flex w-max text-[10px] font-medium uppercase tracking-[0.16em] px-2 py-0.5 rounded ${CATEGORY_BADGES[book.category] || CATEGORY_BADGES["General"]}`}
        >
          {book.category || "General"}
        </span>
        <p className="text-sm text-[#374151] mt-3 leading-relaxed line-clamp-3 flex-1">
          {book.description}
        </p>
        <div className="mt-3 inline-flex items-center text-sm text-[#1B2E7A] font-medium group-hover:gap-2 gap-1 transition-all">
          Abrir libro
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

// Slightly darken/lighten a hex color
function shade(hex, percent) {
  const m = hex.replace("#", "");
  const num = parseInt(m, 16);
  let r = (num >> 16) + Math.round((255 * percent) / 100);
  let g = ((num >> 8) & 0xff) + Math.round((255 * percent) / 100);
  let b = (num & 0xff) + Math.round((255 * percent) / 100);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export default function Library() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/library");
        setBooks(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="library-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">Recursos del equipo</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Biblioteca de manuales y playbooks.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Tus manuales oficiales, guías rápidas y playbooks de NextGen reunidos en un solo lugar.
          Consúltalos desde el piso de ventas para resolver dudas en el momento.
        </p>
      </div>

      {loading ? (
        <p className="text-[#374151]">Cargando biblioteca...</p>
      ) : books.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-md">
          <BookOpen className="h-10 w-10 text-[#6B7280] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#374151]">Aún no hay libros en la biblioteca.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {books.map((b, i) => (
            <BookSpine key={b.id} book={b} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}
